import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { WantedPoster } from "./Home";
import { SEO } from "../components/SEO";
import { usePageContent, resolveMediaUrl } from "../components/PageContent";

const PROSPECT_DEFAULTS = { eyebrow: "On Trial", title: "Prospects" };
const HANGAROUND_DEFAULTS = { eyebrow: "Loitering", title: "Hang Arounds" };
const CREW_SECTION_DEFAULTS = { eyebrow: "Sworn In", title: "The Crew" };

export default function Crew() {
  const [crew, setCrew] = useState([]);
  const header = usePageContent("crew", {
    eyebrow: "By order of the Marshal",
    title: "The Crew",
    body: "Six humans of varying judgment.\nBounties listed in dollars, beverages, or sentimental value.",
  });
  const crewSec = usePageContent("crew", CREW_SECTION_DEFAULTS); // doubles up since header IS the crew section
  const prospectsSec = usePageContent("prospects", PROSPECT_DEFAULTS);
  const hangSec = usePageContent("hang-arounds", HANGAROUND_DEFAULTS);

  useEffect(() => {
    api.get("/crew").then((r) => setCrew(r.data || [])).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const buckets = { crew: [], prospects: [], "hang-arounds": [] };
    for (const c of crew) {
      const s = buckets[c.status] ? c.status : "crew";
      buckets[s].push(c);
    }
    for (const k of Object.keys(buckets)) {
      buckets[k].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    return buckets;
  }, [crew]);

  const sections = [
    { id: "crew", content: { eyebrow: crewSec.eyebrow || "Sworn In", title: "The Crew" } },
    { id: "prospects", content: prospectsSec },
    { id: "hang-arounds", content: hangSec },
  ];

  const bgUrl = resolveMediaUrl(header.image);

  return (
    <div className="bg-card-light min-h-screen" data-testid="crew-page">
      <SEO
        title={header.title || "The Crew"}
        description={header.body}
        image={bgUrl}
        path="/crew"
      />
      <section
        className="relative text-bone py-20 lg:py-28 overflow-hidden"
        style={{
          backgroundColor: "#1C1B1A",
          backgroundImage: header.show_media && bgUrl ? `url(${bgUrl})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="crew-header"
      >
        {header.show_media && bgUrl && header.overlay && (
          <div className="absolute inset-0 bg-asphalt/75 mix-blend-multiply pointer-events-none" />
        )}
        {(!header.show_media || !bgUrl) && (
          <div className="absolute inset-0 opacity-40 pointer-events-none gritty-paper" />
        )}
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
          {header.eyebrow && (
            <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-3" data-testid="crew-header-eyebrow">
              ✦ {header.eyebrow}
            </div>
          )}
          <h1 className="font-western text-5xl sm:text-7xl whitespace-pre-line" data-testid="crew-header-title">
            {header.title}
          </h1>
          {header.subtitle && (
            <p className="font-western text-2xl text-bone/85 max-w-2xl mt-3">{header.subtitle}</p>
          )}
          {header.body && (
            <p className="font-body text-lg text-bone/85 max-w-2xl mt-4 whitespace-pre-line" data-testid="crew-header-description">
              {header.body}
            </p>
          )}
        </div>
      </section>
      <div className="tape-divider" />

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10">
        {sections.map((sec) => {
          const list = grouped[sec.id] || [];
          if (!list.length) return null;
          const content = sec.content;
          return (
            <section key={sec.id} className="py-10" data-testid={`crew-section-${sec.id}`}>
              <div className="flex items-end justify-between flex-wrap gap-3 mb-8 border-b-2 border-asphalt pb-4">
                <div>
                  {content.eyebrow && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-terra mb-1">
                      ✦ {content.eyebrow}
                    </div>
                  )}
                  <h2 className="font-western text-4xl sm:text-5xl whitespace-pre-line">{content.title}</h2>
                  {content.body && (
                    <p className="font-body text-base text-ink mt-2 max-w-2xl whitespace-pre-line">{content.body}</p>
                  )}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink">
                  {list.length} on the rolls
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {list.map((c, i) => (
                  <WantedPoster
                    crew={c}
                    key={c.id}
                    testid={`crew-poster-${sec.id}-${i}`}
                    linkable
                  />
                ))}
              </div>
            </section>
          );
        })}

        {crew.length === 0 && (
          <div className="py-20 text-center font-mono text-xs uppercase tracking-[0.3em] text-ink" data-testid="crew-empty">
            ✦ The rolls are empty. Add a crew member from the admin dashboard.
          </div>
        )}
      </div>
    </div>
  );
}
